/**
 * OLFARIA cart — drawer, line updates, quick add and live cart regions.
 *
 * <cart-drawer> keeps the public contract Dawn's product-form.js relies on
 * (getSectionsToRender, renderContents, setActiveElement, open, close, .active / .is-empty),
 * so the product page form works unchanged. Everything else hangs off the design's hooks:
 *
 *   #cart-icon-bubble / [data-cart-toggle]   open the drawer
 *   [data-cart-close]                        close it
 *   [data-line-qty] + .ol-qty__btn           change a line quantity (line addressed by key)
 *   [data-line-remove]                       remove a line
 *   button[form="ol-quick-add"]              add one variant (cards, cart recommendations)
 *   [data-ol-cart-live="<section id>"]       regions re-rendered after every cart change
 *
 * One request per cart change: the drawer, the header count and every live region on the
 * page come back in the same response via the Section Rendering API.
 */
(() => {
  const DRAWER_SECTION = 'cart-drawer';
  const BUBBLE_SECTION = 'cart-icon-bubble';
  const OWN_SOURCES = ['ol-cart', 'product-form'];

  const getDrawer = () => document.querySelector('cart-drawer');
  const getLiveRegions = () => Array.from(document.querySelectorAll('[data-ol-cart-live]'));
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const isCartPage = () => Boolean(document.querySelector('[data-ol-cart-page]'));

  function getSectionIds() {
    const ids = [BUBBLE_SECTION, ...getLiveRegions().map((region) => region.dataset.olCartLive)];
    if (getDrawer()) ids.unshift(DRAWER_SECTION);
    // The Section Rendering API accepts at most five sections per request.
    return [...new Set(ids)].slice(0, 5);
  }

  /*
   * Swaps a region's markup but lets the discount segments and the shipping bar animate from
   * their previous state: the fresh nodes start where the old ones were, then move on.
   */
  const SEG_ON = 'ol-segments__seg--on';
  const SEG_STEP = 150;

  function replaceAnimated(container, html) {
    const oldSegs = Array.from(container.querySelectorAll('.ol-segments__seg'), (seg) => seg.classList.contains(SEG_ON));
    const oldBars = Array.from(container.querySelectorAll('.ol-bar__fill'), (bar) => bar.style.width);
    container.innerHTML = html;

    const segs = Array.from(container.querySelectorAll('.ol-segments__seg'));
    const bars = Array.from(container.querySelectorAll('.ol-bar__fill'));
    const newSegs = segs.map((seg) => seg.classList.contains(SEG_ON));
    const newBars = bars.map((bar) => bar.style.width);
    const segsMatch = segs.length === oldSegs.length;
    const barsMatch = bars.length === oldBars.length;
    if (!segsMatch && !barsMatch) return;

    if (segsMatch) segs.forEach((seg, i) => seg.classList.toggle(SEG_ON, oldSegs[i]));
    if (barsMatch) bars.forEach((bar, i) => (bar.style.width = oldBars[i]));
    void container.offsetWidth;

    if (segsMatch) {
      // Fill left to right, empty right to left, one segment after the other.
      const changed = segs.filter((seg, i) => oldSegs[i] !== newSegs[i]);
      const filling = changed.length && newSegs[segs.indexOf(changed[0])];
      (filling ? changed : changed.reverse()).forEach((seg, step) => {
        seg.style.transitionDelay = `${step * SEG_STEP}ms`;
      });
      segs.forEach((seg, i) => seg.classList.toggle(SEG_ON, newSegs[i]));
    }
    if (barsMatch) bars.forEach((bar, i) => (bar.style.width = newBars[i]));
  }

  function renderSections(sections) {
    if (!sections) return;

    const drawer = getDrawer();
    if (drawer && sections[DRAWER_SECTION]) {
      const source = parseHTML(sections[DRAWER_SECTION]).querySelector('[data-drawer-inner]');
      if (source && drawer.inner) replaceAnimated(drawer.inner, source.innerHTML);
    }

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections[BUBBLE_SECTION]) {
      const source = parseHTML(sections[BUBBLE_SECTION]).querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    getLiveRegions().forEach((region) => {
      const id = region.dataset.olCartLive;
      if (!sections[id]) return;
      const source = parseHTML(sections[id]).querySelector(`[data-ol-cart-live="${id}"]`);
      if (source) replaceAnimated(region, source.innerHTML);
    });
  }

  function refresh() {
    return fetch(`${window.location.pathname}?sections=${getSectionIds().join(',')}`)
      .then((response) => response.json())
      .then(renderSections)
      .catch((error) => console.error(error));
  }

  function setBusy(busy) {
    document.querySelectorAll('.ol-cart__lines').forEach((lines) => lines.setAttribute('aria-busy', busy));
  }

  function showError(message) {
    document.querySelectorAll('[data-cart-error]').forEach((element) => {
      element.textContent = message || '';
      element.hidden = !message;
    });
  }

  /* Storefront standard events, mirroring Dawn's cart.js / product-form.js. Never blocks the cart. */
  function createLinesUpdateEvent(target, detail) {
    try {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent) return null;

      const deferred = CartLinesUpdateEvent.createPromise();
      const dispatcher = target && target.isConnected ? target : document;
      dispatcher.dispatchEvent(new CartLinesUpdateEvent({ ...detail, promise: deferred.promise }));
      return deferred;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  function resolveLinesUpdateEvent(deferred, cart) {
    if (!deferred) return;
    try {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
    } catch (error) {
      deferred.reject(error);
    }
  }

  function dispatchCartError(message, code) {
    const { CartErrorEvent } = window.StandardEvents || {};
    if (CartErrorEvent) document.dispatchEvent(new CartErrorEvent({ error: message, code }));
  }

  /* Puts focus back on the control that triggered a re-render (its DOM node was replaced). */
  function restoreFocus(origin) {
    if (!origin) return;

    const drawer = getDrawer();
    const scope = origin.inDrawer ? drawer && drawer.inner : document.querySelector(`[data-ol-cart-live="${origin.region}"]`);
    if (!scope) return;

    const input = scope.querySelector(`[data-line-qty][data-line-key="${origin.key}"]`);
    const control = input && origin.name ? input.parentElement.querySelector(`[name="${origin.name}"]`) : input;
    const target = control || scope.querySelector('a[href], button:not([disabled])');

    if (origin.inDrawer && drawer.classList.contains('active')) {
      trapFocus(drawer.inner, target || drawer.inner);
    } else if (target) {
      target.focus();
    }
  }

  function describeOrigin(element, name) {
    const region = element.closest('[data-ol-cart-live]');
    return {
      key: element.dataset.lineKey,
      name,
      inDrawer: Boolean(element.closest('cart-drawer')),
      region: region ? region.dataset.olCartLive : null,
    };
  }

  function changeLine(element, quantity, name) {
    const key = element.dataset.lineKey;
    const origin = describeOrigin(element, name);
    const deferred = createLinesUpdateEvent(element, {
      action: quantity === 0 ? 'remove' : 'update',
      context: 'cart',
      lines: [{ id: key, quantity }],
    });

    setBusy(true);
    showError('');

    const body = JSON.stringify({
      id: key,
      quantity,
      sections: getSectionIds(),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((state) => {
        if (state.errors || state.status) {
          const message = state.errors || state.description || state.message || window.cartStrings.error;
          dispatchCartError(message, 'INVALID');
          if (deferred) deferred.reject(new Error(message));
          return refresh().then(() => showError(message));
        }

        renderSections(state.sections);

        const drawer = getDrawer();
        if (drawer) drawer.classList.toggle('is-empty', state.item_count === 0);

        const line = state.items.find((item) => item.key === key);
        if (line && line.quantity !== quantity) {
          showError(window.cartStrings.quantityError.replace('[quantity]', line.quantity));
        }

        restoreFocus(origin);
        resolveLinesUpdateEvent(deferred, state);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ol-cart', cartData: state, variantId: element.dataset.variantId });
      })
      .catch((error) => {
        console.error(error);
        showError(window.cartStrings.error);
        dispatchCartError(window.cartStrings.error, 'SERVICE_UNAVAILABLE');
        if (deferred) deferred.reject(error);
      })
      .finally(() => setBusy(false));
  }

  /* opener: where focus returns when the drawer closes (the card button for a size chooser option) */
  function addVariant(variantId, button, opener = button) {
    if (button.getAttribute('aria-disabled') === 'true') return Promise.resolve();
    button.setAttribute('aria-disabled', 'true');

    const drawer = getDrawer();
    const deferred = createLinesUpdateEvent(button, {
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
    });

    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: getSectionIds(),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_add_url}.js`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((state) => {
        if (state.status) {
          const message = state.description || state.message || window.cartStrings.error;
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'ol-cart',
            productVariantId: variantId,
            errors: state.errors || state.description,
            message: state.message,
          });
          dispatchCartError(message, 'INVALID');
          if (deferred) deferred.reject(new Error(message));

          if (!drawer) return window.alert(message);
          showError(message);
          if (!isCartPage()) drawer.open(opener);
          return;
        }

        if (deferred) {
          fetch(`${routes.cart_url}.json`)
            .then((response) => response.json())
            .then((cart) => resolveLinesUpdateEvent(deferred, cart))
            .catch((error) => deferred.reject(error));
        }

        if (!drawer) {
          window.location = routes.cart_url;
          return;
        }

        drawer.setActiveElement(opener);
        drawer.renderContents(state, { open: !isCartPage() });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ol-cart', productVariantId: variantId, cartData: state });
      })
      .catch((error) => {
        console.error(error);
        dispatchCartError(error.message || 'Network error', 'SERVICE_UNAVAILABLE');
        if (deferred) deferred.reject(error);
      })
      .finally(() => {
        button.removeAttribute('aria-disabled');
      });
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    }

    get inner() {
      return this.querySelector('[data-drawer-inner]');
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      if (this.classList.contains('active')) return;

      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      requestAnimationFrame(() => trapFocus(this.inner, this.inner));

      const viewEvent = this.querySelector('cart-view-event');
      if (viewEvent && typeof viewEvent.dispatchViewEvent === 'function') viewEvent.dispatchViewEvent();
    }

    close() {
      if (!this.classList.contains('active')) return;

      this.classList.remove('active');
      removeTrapFocus(this.activeElement && this.activeElement.isConnected ? this.activeElement : null);
      document.body.classList.remove('overflow-hidden');
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    getSectionsToRender() {
      return getSectionIds().map((id) => ({ id }));
    }

    renderContents(parsedState, { open = true } = {}) {
      renderSections(parsedState.sections);
      this.classList.remove('is-empty');

      if (this.classList.contains('active')) {
        trapFocus(this.inner, this.inner);
      } else if (open) {
        setTimeout(() => this.open());
      }
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* Quantity: one pending request per line, debounced like Dawn's cart. */
  const pendingChanges = new Map();

  function queueChange(input, name) {
    const key = input.dataset.lineKey;
    clearTimeout(pendingChanges.get(key));
    pendingChanges.set(
      key,
      setTimeout(() => {
        pendingChanges.delete(key);
        changeLine(input, Math.max(0, parseInt(input.value, 10) || 0), name);
      }, ON_CHANGE_DEBOUNCE_TIMER)
    );
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-line-qty]');
    if (input) queueChange(input, input.dataset.stepName);
  });

  document.addEventListener('click', (event) => {
    const step = event.target.closest('.ol-qty__btn');
    if (step) {
      const input = step.parentElement.querySelector('[data-line-qty]');
      if (!input) return;

      event.preventDefault();
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + (step.name === 'plus' ? 1 : -1));
      if (next === current) return;

      input.value = next;
      input.dataset.stepName = step.name;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const remove = event.target.closest('[data-line-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove, 0);
      return;
    }

    const drawer = getDrawer();
    if (!drawer) return;

    if (event.target.closest('[data-cart-close]')) {
      event.preventDefault();
      drawer.close();
      return;
    }

    const toggle = event.target.closest('[data-cart-toggle]');
    if (toggle) {
      event.preventDefault();
      drawer.open(toggle);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.code !== 'Space') return;
    const toggle = event.target.closest('[data-cart-toggle]');
    const drawer = getDrawer();
    if (!toggle || !drawer) return;

    event.preventDefault();
    drawer.open(toggle);
  });

  document.addEventListener('submit', (event) => {
    // not form.id: the quick-add buttons are named "id" and shadow that property
    if (!event.target.matches('#ol-quick-add')) return;

    const button = event.submitter;
    if (!button || !button.value) return;

    event.preventDefault();
    const panel = button.closest('.ol-sizes');
    if (!panel) return addVariant(button.value, button);

    // A chosen size stays busy in its chooser until the drawer opens, then the chooser closes.
    const toggle = panel.parentElement.querySelector('[data-ol-sizes-toggle]');
    addVariant(button.value, button, toggle || button).then(() => closeSizes(panel));
  });

  /*
   * Card size chooser (snippets/ol-card.liquid): with more than one sample size in stock the
   * card button opens the sizes with their prices instead of adding one. One chooser is open at
   * a time; the button again, the close button, Escape, a click outside or focus leaving close it.
   */
  let openSizes = null;

  function closeSizes(panel, returnFocus = false) {
    if (!openSizes || (panel && openSizes.panel !== panel)) return;
    const { toggle } = openSizes;
    openSizes.panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    openSizes = null;
    if (returnFocus) toggle.focus();
  }

  function openSizesFor(toggle) {
    const panel = toggle.parentElement.querySelector('.ol-sizes');
    if (!panel) return;
    closeSizes();
    if (!panel.id) panel.id = `OlSizes-${Math.random().toString(36).slice(2, 10)}`;
    toggle.setAttribute('aria-controls', panel.id);
    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
    openSizes = { toggle, panel };
    const first =
      panel.querySelector('.ol-sizes__opt--suggested:not(:disabled)') || panel.querySelector('.ol-sizes__opt:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-ol-sizes-toggle]');
    if (toggle) {
      // the button submits the quick-add size only without JS
      event.preventDefault();
      if (openSizes && openSizes.toggle === toggle) closeSizes();
      else openSizesFor(toggle);
      return;
    }
    if (event.target.closest('[data-ol-sizes-close]')) {
      closeSizes(null, true);
      return;
    }
    if (openSizes && !openSizes.panel.contains(event.target)) closeSizes();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openSizes) return;
    event.preventDefault();
    closeSizes(null, true);
  });

  document.addEventListener('focusout', (event) => {
    // relatedTarget is null for clicks on nothing focusable; the click handler covers those.
    // Safari does not focus a tapped button but its nearest focusable ancestor (<main
    // tabindex="-1">), so focus moving to an element that contains the chooser is no leaving.
    const next = event.relatedTarget;
    if (!openSizes || !next || next.contains(openSizes.panel)) return;
    if (!openSizes.panel.parentElement.contains(next)) closeSizes();
  });

  /* Cart changes made by other components (Dawn's stock sections, apps using the pub/sub bus). */
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event && OWN_SOURCES.includes(event.source)) return;
    return refresh();
  });

  /*
   * Card quick add follows the sample size the cart holds most of, else the first available
   * sample, else the first available variant (snippets/ol-quick-variant.liquid renders the same
   * choice). Full bottles never set the preference. Cards are not re-rendered on cart changes,
   * so each one carries its variants (with a sample flag) in data-ol-variants and is re-pointed
   * here.
   */
  function sizeQuantities(cart) {
    const quantities = new Map();
    cart.items.forEach((item) => {
      const size = item.options_with_values && item.options_with_values[0] && item.options_with_values[0].value;
      if (size) quantities.set(size, (quantities.get(size) || 0) + item.quantity);
    });
    return quantities;
  }

  function pointCard(card, quantities) {
    let variants;
    try {
      variants = JSON.parse(card.dataset.olVariants);
    } catch (error) {
      return;
    }
    // Only the card's sample sizes count, so a full bottle in the cart is ignored.
    let preferred = null;
    variants.forEach((v) => {
      const quantity = quantities.get(v.size) || 0;
      if (v.sample && quantity > 0 && (!preferred || quantity > quantities.get(preferred.size))) preferred = v;
    });
    const variant =
      (preferred && preferred.available && preferred) ||
      variants.find((v) => v.sample && v.available) ||
      variants.find((v) => v.available) ||
      variants[0];
    // A size chooser only hints at the cart's size; it never preselects one.
    const suggested = preferred && preferred.available ? String(preferred.id) : null;
    card.querySelectorAll('.ol-sizes__opt').forEach((option) => {
      option.classList.toggle('ol-sizes__opt--suggested', option.value === suggested);
    });

    const button = card.querySelector('.ol-card__quick');
    if (!variant || !button || button.value === String(variant.id)) return;

    button.value = variant.id;
    button.disabled = !variant.available;
    if (button.hasAttribute('data-ol-sizes-toggle')) return;
    const template = variant.sample ? button.dataset.labelTemplate : button.dataset.labelTemplateFull;
    if (variant.available && template) {
      button.textContent = template.replace('__SIZE__', variant.size.replace(/\s+/g, '').toUpperCase());
    }
  }

  function pointCards() {
    const cards = document.querySelectorAll('.ol-card[data-ol-variants]');
    if (!cards.length) return;
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const quantities = sizeQuantities(cart);
        cards.forEach((card) => pointCard(card, quantities));
      })
      .catch((error) => console.error(error));
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, pointCards);

  /* Back/forward cache restores a stale cart. */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      refresh();
      pointCards();
    }
  });

  const toggle = document.querySelector('[data-cart-toggle]');
  if (toggle && getDrawer()) {
    toggle.setAttribute('role', 'button');
    toggle.setAttribute('aria-haspopup', 'dialog');
  }
})();
